import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// ui
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import DoneIcon from "@mui/icons-material/Done";
// style
import { createStyles, makeStyles, styled, alpha } from "@material-ui/styles";
// component
import ColorPickerBasic from "../colorPicker/ColorPickerBasic";
import ButtonMain from "../button/ButtonMain";
// utils

//
const useStyles = makeStyles((theme) =>
  createStyles({
    root: {},
  })
);

const StyledColorSettingBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  minWidth: "136px",
}));

const StyledColorBlock = styled(Box)(({ theme }) => ({
  width: "16px",
  height: "16px",
  borderRadius: "100%",
}));

const colorsColorful = [
  "#ff5252",
  "#ff814a",
  "#ffb629",
  "#fce262",
  "#b3e364",
  "#56d681",
  "#25dae0",
  "#3bbff0",
  "#459bff",
  "#9280ff",
  "#bb81ff",
  "#ff7ce1",
];

const colorsGrey = [
  "#ffffff",
  "#ededed",
  "#c9c9c9",
  "#909090",
  "#6c6c6c",
  "#121212",
];

//
const ColorCirclePopup = ({
  variant,
  colors,
  colorBoxProps,
  onColorFocus,
  popoverProps,
  colorPickerProps,
  onFinish,
}) => {
  // style
  const classes = useStyles();
  // props
  const { onChangeComplete, onChange, ...restColorPickerProps } =
    colorPickerProps;
  // local state
  const [anchorEl, setAnchorEl] = useState(null);
  const [colorFocusIdx, setColorFocusIdx] = useState(null);
  const [localColors, setLocalColors] = useState(colors);
  const [localColorsMemo, setLocalColorsMemo] = useState(colors);
  //
  const open = Boolean(anchorEl);
  const isColorsDifferent =
    JSON.stringify(localColors) !== JSON.stringify(localColorsMemo);
  //

  useEffect(() => {
    setLocalColors(colors);
    setLocalColorsMemo(colors);
  }, [colors]);
  //
  const handleColorFocus = (colorIdx, colorProps) => (event) => {
    setColorFocusIdx(colorIdx);
    setAnchorEl(event.currentTarget);
    if (onColorFocus) onColorFocus(colorIdx, colorProps);
  };
  const handleClose = () => {
    if (isColorsDifferent) {
      setLocalColors(localColorsMemo);
    }
    setAnchorEl(null);
  };

  const getColorPickerProps = () => {
    // 若有自帶 colorPickerProps.colors, 則優先使用 colorPickerProps.colors
    if (
      Array.isArray(colorPickerProps.colors) &&
      colorPickerProps.colors.length > 0
    ) {
      return colorPickerProps;
    }
    let customColors = [];
    let safeVariantList = variant;
    if (!Array.isArray(variant)) {
      safeVariantList = [variant];
    }
    safeVariantList.forEach((vr) => {
      if (vr === "colorful") {
        customColors = [...customColors, ...colorsColorful];
      } else if (vr === "grey") {
        customColors = [...customColors, ...colorsGrey];
      } else if (vr === "colorful-grey") {
        customColors = [...customColors, ...colorsColorful, ...colorsGrey];
      } else {
        customColors = [...customColors, ...colorsColorful, ...colorsGrey];
      }
    });
    return { ...restColorPickerProps, colors: customColors };
  };

  const handleColorChangeComplete = (color, event) => {
    const newColors = localColors.map((item, idx) => {
      if (idx === colorFocusIdx) {
        return {
          sx: {
            ...item.sx,
            bgcolor: color.hex,
          },
        };
      }
      return item;
    });
    setLocalColors(newColors);
    if (onChangeComplete) onChangeComplete(color, event, newColors);
  };

  const handleColorChange = (color, event) => {};

  const handleOk = () => {
    setLocalColorsMemo(localColors);
    if (onFinish) {
      onFinish(localColors);
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  return (
    <Box>
      <StyledColorSettingBox {...colorBoxProps}>
        {Array.isArray(localColors) &&
          localColors.map((singleColorProps, idx) => (
            <IconButton
              key={idx.toString()}
              onClick={handleColorFocus(idx, singleColorProps)}
              size="small"
              // sx={{ ml: 2, mt: 2 }}
              aria-controls={open ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
            >
              <StyledColorBlock {...singleColorProps} />
            </IconButton>
          ))}
      </StyledColorSettingBox>
      <Popover
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        {...popoverProps}
      >
        <Box px={2} pt={1} pb={2}>
          <ColorPickerBasic
            {...getColorPickerProps()}
            onChange={handleColorChange}
            onChangeComplete={handleColorChangeComplete}
          />
          <Box display="flex" justifyContent="space-between">
            <ButtonMain
              buttonProps={{
                variant: isColorsDifferent ? "contained" : "disabled",
                size: "small",
                startIcon: <DoneIcon />,
                text: "OK",
                onClick: handleOk,
                disabled: !isColorsDifferent,
              }}
            />
            <ButtonMain
              buttonProps={{
                variant: "neutral",
                size: "small",
                text: "close",
                onClick: handleCancel,
              }}
            />
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

ColorCirclePopup.propTypes = {
  variant: PropTypes.oneOf(["colorful", "grey", "colorful-grey"]),
  colors: PropTypes.arrayOf(PropTypes.any),
  onColorFocus: PropTypes.func,
  onFinish: PropTypes.func,
  popoverProps: PropTypes.objectOf(PropTypes.any),
  colorBoxProps: PropTypes.objectOf(PropTypes.any),
  colorPickerProps: PropTypes.objectOf(PropTypes.any),
};

ColorCirclePopup.defaultProps = {
  variant: "colorful",
  colors: [],
  onColorFocus: () => null,
  onFinish: () => null,
  popoverProps: {},
  colorBoxProps: {},
  colorPickerProps: {},
};

export default ColorCirclePopup;
